import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  FileText, Upload, Download, Eye, Trash2, CheckCircle, XCircle,
  Loader2, FolderOpen, GraduationCap, Briefcase, User, File,
  AlertCircle, Shield, Search
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const CATEGORY_ICONS = {
  personal: User,
  educational: GraduationCap,
  professional: Briefcase,
  other: FolderOpen
};

const CATEGORY_COLORS = {
  personal: 'bg-blue-100 text-blue-800 border-blue-200',
  educational: 'bg-purple-100 text-purple-800 border-purple-200',
  professional: 'bg-green-100 text-green-800 border-green-200',
  other: 'bg-slate-100 text-slate-800 border-slate-200'
};

export default function Documents() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    category: '',
    document_type: '',
    document_name: '',
    file: null,
    file_data: '',
    file_name: '',
    mime_type: ''
  });

  const userRole = user?.role || 'employee';
  const isEmployee = userRole === 'employee';
  const canVerify = ['hr', 'admin', 'super_admin'].includes(userRole);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [docsRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/documents`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/document-categories`, { headers, credentials: 'include' })
      ]);

      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocuments(data);
      }
      if (catsRes.ok) {
        const data = await catsRes.json();
        setCategories(data);
      }
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and image files (JPG, PNG) are allowed');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setUploadForm({
        ...uploadForm,
        file: file,
        file_data: base64,
        file_name: file.name,
        mime_type: file.type
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.category || !uploadForm.document_type || !uploadForm.document_name || !uploadForm.file_data) {
      toast.error('Please fill all required fields and select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          category: uploadForm.category,
          document_type: uploadForm.document_type,
          document_name: uploadForm.document_name,
          file_data: uploadForm.file_data,
          file_name: uploadForm.file_name,
          mime_type: uploadForm.mime_type
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setIsUploadOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setUploadForm({
      category: '',
      document_type: '',
      document_name: '',
      file: null,
      file_data: '',
      file_name: '',
      mime_type: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleView = async (document) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/documents/${document.document_id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedDocument(data);
        setIsViewOpen(true);
      } else {
        toast.error('Failed to load document');
      }
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleDownload = (document) => {
    if (!document.file_data) {
      toast.error('Document data not available');
      return;
    }

    const link = window.document.createElement('a');
    link.href = `data:${document.mime_type};base64,${document.file_data}`;
    link.download = document.file_name;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    toast.success('Document downloaded');
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document deleted');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleVerify = async (documentId) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/documents/${documentId}/verify`, {
        method: 'PUT',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document verified');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to verify document');
      }
    } catch (error) {
      toast.error('Failed to verify document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.document_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.file_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || doc.category === activeTab;
    return matchesSearch && matchesTab;
  });

  const documentsByCategory = {
    personal: documents.filter(d => d.category === 'personal'),
    educational: documents.filter(d => d.category === 'educational'),
    professional: documents.filter(d => d.category === 'professional'),
    other: documents.filter(d => d.category === 'other')
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            {isEmployee ? 'My Documents' : 'Employee Documents'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {isEmployee ? 'Upload and manage your personal & educational documents' : 'View and verify employee documents'}
          </p>
        </div>
        {isEmployee && (
          <Dialog open={isUploadOpen} onOpenChange={(open) => { setIsUploadOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="upload-document-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#DC2626]" />
                  Upload Document
                </DialogTitle>
                <DialogDescription>
                  Upload your personal, educational, or professional documents
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Category <span className="text-red-500">*</span></Label>
                  <Select
                    value={uploadForm.category}
                    onValueChange={(value) => setUploadForm({ ...uploadForm, category: value, document_type: '' })}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Document Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={uploadForm.document_type}
                    onValueChange={(value) => setUploadForm({ ...uploadForm, document_type: value })}
                    disabled={!uploadForm.category}
                  >
                    <SelectTrigger data-testid="document-type-select">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {uploadForm.category && categories[uploadForm.category]?.types.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Document Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={uploadForm.document_name}
                    onChange={(e) => setUploadForm({ ...uploadForm, document_name: e.target.value })}
                    placeholder="e.g., My PAN Card, 10th Marksheet"
                    data-testid="document-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Select File <span className="text-red-500">*</span></Label>
                  <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      data-testid="file-input"
                    />
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <File className="w-8 h-8 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium text-sm">{uploadForm.file_name}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(uploadForm.file.size)}</p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={resetForm}>
                          <XCircle className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                          Choose File
                        </Button>
                        <p className="text-xs text-slate-500 mt-2">PDF, JPG, PNG (Max 5MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={uploading} data-testid="submit-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Documents</p>
                <p className="text-2xl font-bold">{documents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        {Object.entries(categories).map(([key, cat]) => {
          const Icon = CATEGORY_ICONS[key] || FolderOpen;
          return (
            <Card key={key} className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{cat.name}</p>
                    <p className="text-2xl font-bold">{documentsByCategory[key]?.length || 0}</p>
                  </div>
                  <Icon className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="all">All ({documents.length})</TabsTrigger>
          <TabsTrigger value="personal">Personal ({documentsByCategory.personal?.length || 0})</TabsTrigger>
          <TabsTrigger value="educational">Educational ({documentsByCategory.educational?.length || 0})</TabsTrigger>
          <TabsTrigger value="professional">Professional ({documentsByCategory.professional?.length || 0})</TabsTrigger>
          <TabsTrigger value="other">Other ({documentsByCategory.other?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#DC2626]" />
                {activeTab === 'all' ? 'All Documents' : categories[activeTab]?.name || 'Documents'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium">No documents found</h3>
                  <p className="text-slate-500 mb-4">
                    {isEmployee ? 'Start by uploading your first document' : 'No documents to display'}
                  </p>
                  {isEmployee && (
                    <Button className="btn-primary" onClick={() => setIsUploadOpen(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => {
                      const Icon = CATEGORY_ICONS[doc.category] || FolderOpen;
                      return (
                        <TableRow key={doc.document_id} data-testid={`document-row-${doc.document_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[doc.category]}`}>
                                <Icon className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium">{doc.document_name}</p>
                                <p className="text-xs text-slate-500">{doc.file_name}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{doc.document_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={CATEGORY_COLORS[doc.category]}>
                              {categories[doc.category]?.name || doc.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                          </TableCell>
                          <TableCell>
                            {doc.is_verified ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleView(doc)}
                                data-testid={`view-doc-${doc.document_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {canVerify && !doc.is_verified && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleVerify(doc.document_id)}
                                  data-testid={`verify-doc-${doc.document_id}`}
                                >
                                  <Shield className="w-4 h-4" />
                                </Button>
                              )}
                              {(isEmployee || canVerify) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleDelete(doc.document_id)}
                                  data-testid={`delete-doc-${doc.document_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Document Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#DC2626]" />
              {selectedDocument?.document_name}
            </DialogTitle>
          </DialogHeader>
          {selectedDocument && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Type:</span>
                  <span className="ml-2 font-medium">{selectedDocument.document_type}</span>
                </div>
                <div>
                  <span className="text-slate-500">Category:</span>
                  <span className="ml-2 font-medium">{categories[selectedDocument.category]?.name}</span>
                </div>
                <div>
                  <span className="text-slate-500">Uploaded:</span>
                  <span className="ml-2">{new Date(selectedDocument.uploaded_at).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-slate-500">Status:</span>
                  <span className="ml-2">
                    {selectedDocument.is_verified ? (
                      <Badge className="bg-green-100 text-green-800">Verified</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800">Pending Verification</Badge>
                    )}
                  </span>
                </div>
              </div>

              {/* Document Preview */}
              <div className="border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900" style={{ height: '60vh' }}>
                {selectedDocument.mime_type?.startsWith('image/') ? (
                  <img
                    src={`data:${selectedDocument.mime_type};base64,${selectedDocument.file_data}`}
                    alt={selectedDocument.document_name}
                    className="w-full h-full object-contain"
                  />
                ) : selectedDocument.mime_type === 'application/pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${selectedDocument.file_data}`}
                    title={selectedDocument.document_name}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                      <p>Preview not available for this file type</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsViewOpen(false)}>Close</Button>
                <Button className="btn-primary" onClick={() => handleDownload(selectedDocument)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">Document Security</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Your documents are securely stored and can only be viewed by authorized personnel 
                (HOD, Principal, and HR of your institute). All documents are encrypted and access is logged.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
